import { useState, useMemo } from 'react';

/**
 * useFiltrosReportes - Custom hook dedicado al procesamiento y lógica de filtros del BI.
 * 
 * @param {Object} dbState - Objeto con los arrays crudos de cada colección (polizas, cobranza, etc.)
 * @param {string} moduloRaiz - Clave del módulo activo (POLIZAS, COBRANZA, RENOVACIONES, etc.)
 * @param {boolean} isDbLoaded - Indica si la consulta a la base de datos ya finalizó
 * @param {string} debouncedBusqueda - Texto sanitizado de la barra de búsqueda global
 */
export const useFiltrosReportes = ({ dbState, moduloRaiz, isDbLoaded, debouncedBusqueda }) => {
    
    // --- ESTADO GENERAL DE FILTROS SELECCIONADOS ---
    const [multiFiltros, setMultiFiltros] = useState({
        // Generales
        compania: [],
        riesgo: [],
        subriesgo: [],
        moneda: [],
        formaPago: [],

        // Estructura de Pólizas y Comercial
        canal: [],
        canalNombre: [],
        subcanalNombre: [],
        estadoEmision: [],
        motivo: [],
        subOperacion: [],
        tipoFacturacion: [],
        vendedor: [],
        suscriptor: [],

        // Exclusivos Cobranza
        estadoGestionCobranza: [],
        quienCobro: [],
        quienInformo: [],
        estadoDeuda: [],

        // Exclusivos Renovaciones
        estadoGestionRenovacion: [],
        quienRenovo: [],
        tipoRenovacion: [],
        formaRenovacion: []
    });

    // Helper: Limpiar todos los filtros activos
    const resetFiltros = () => {
        setMultiFiltros({
            compania: [], riesgo: [], subriesgo: [], moneda: [], formaPago: [],
            canal: [], canalNombre: [], subcanalNombre: [], estadoEmision: [],
            motivo: [], subOperacion: [], tipoFacturacion: [], vendedor: [], suscriptor: [],
            estadoGestionCobranza: [], quienCobro: [], quienInformo: [], estadoDeuda: [],
            estadoGestionRenovacion: [], quienRenovo: [], tipoRenovacion: [], formaRenovacion: []
        });
    };

    // --- 1. EXTRACCIÓN DE OPCIONES ÚNICAS PARA CADA DESPLEGABLE ---
    const opcionesFiltrosUnicas = useMemo(() => {
        if (!isDbLoaded) return {};

        const getUnique = (arr, fieldOrFn) => {
            if (!arr || !Array.isArray(arr) || arr.length === 0) return [];
            const uniqueSet = new Set();
            
            arr.forEach(item => {
                let val = '';
                if (typeof fieldOrFn === 'function') {
                    val = fieldOrFn(item);
                } else if (fieldOrFn === 'canalNombre') {
                    val = item.canalNombre || item.subcanal;
                } else if (fieldOrFn === 'compania') {
                    val = item.compania || item.companiaExpirante;
                } else if (fieldOrFn === 'estadoGestionCobranza') {
                    val = item.estado || item.estadoGestion;
                } else {
                    val = item[fieldOrFn];
                }
                
                if (val !== undefined && val !== null && String(val).trim() !== '' && String(val).trim() !== '-') {
                    uniqueSet.add(String(val).toUpperCase().trim());
                }
            });
            
            return [...uniqueSet].sort((a, b) => a.localeCompare(b));
        };

        // Identificación certera de la colección activa según el módulo
        let poolDeMuestra = [];
        if (moduloRaiz === 'COBRANZA') {
            poolDeMuestra = dbState.cobranza || [];
        } else if (moduloRaiz === 'RENOVACIONES') {
            poolDeMuestra = dbState.renovaciones || [];
        } else if (moduloRaiz === 'ANULACIONES') {
            poolDeMuestra = dbState.anulaciones || [];
        } else if (moduloRaiz === 'SINIESTROS') {
            poolDeMuestra = dbState.siniestros || [];
        } else if (moduloRaiz === 'ENDOSOS') {
            poolDeMuestra = dbState.endosos || [];
        } else if (moduloRaiz === 'REFACTURACIONES') {
            poolDeMuestra = dbState.refacturaciones || [];
        } else {
            poolDeMuestra = dbState.polizas || [];
        }

        return {
            // Generales
            compania: getUnique(poolDeMuestra, 'compania'),
            riesgo: getUnique(poolDeMuestra, 'riesgo'),
            subriesgo: getUnique(poolDeMuestra, 'subriesgo'),
            moneda: getUnique(poolDeMuestra, 'moneda'),
            formaPago: getUnique(poolDeMuestra, 'formaPago'),

            // Cobranza
            estadoGestionCobranza: getUnique(poolDeMuestra, 'estadoGestionCobranza'),
            quienCobro: getUnique(poolDeMuestra, 'quienCobro'),
            quienInformo: getUnique(poolDeMuestra, 'quienInformo'),
            estadoDeuda: getUnique(poolDeMuestra, 'estadoDeuda'),

            // Estructura de Pólizas y Comercial (Soporta póliza previa y nueva vinculada)
            canal: getUnique(poolDeMuestra, 'canal'),
            canalNombre: getUnique(poolDeMuestra, 'canalNombre'),
            subcanalNombre: getUnique(poolDeMuestra, 'subcanalNombre'),
            estadoEmision: getUnique(poolDeMuestra, 'estadoEmision'),
            motivo: getUnique(poolDeMuestra, item => item.motivoNuevo || item.motivo || item.tipoOperacion),
            subOperacion: getUnique(poolDeMuestra, item => item.subOperacionNueva || item.subOperacion),
            tipoFacturacion: getUnique(poolDeMuestra, 'tipoFacturacion'),
            vendedor: getUnique(poolDeMuestra, 'vendedor'),
            suscriptor: getUnique(poolDeMuestra, 'suscriptor'),

            // Renovaciones
            estadoGestionRenovacion: getUnique(poolDeMuestra, 'estadoGestionRenovacion'),
            quienRenovo: getUnique(poolDeMuestra, 'quienRenovo'),
            tipoRenovacion: getUnique(poolDeMuestra, 'tipoRenovacion'),
            formaRenovacion: getUnique(poolDeMuestra, 'formaRenovacion')
        };
    }, [dbState, isDbLoaded, moduloRaiz]);

    // --- 2. FILTRADO DINÁMICO EN MEMORIA ---
    const registrosProcesados = useMemo(() => {
        if (!isDbLoaded) return [];

        let base = [];
        if (moduloRaiz === 'POLIZAS') base = dbState.polizas;
        else if (moduloRaiz === 'COBRANZA') base = dbState.cobranza;
        else if (moduloRaiz === 'SINIESTROS') base = dbState.siniestros;
        else if (moduloRaiz === 'ENDOSOS') base = dbState.endosos;
        else if (moduloRaiz === 'REFACTURACIONES') base = dbState.refacturaciones;
        else if (moduloRaiz === 'ANULACIONES') base = dbState.anulaciones;
        else if (moduloRaiz === 'RENOVACIONES') base = dbState.renovaciones;

        return (base || []).filter(row => {
            // Búsqueda global de texto
            if (debouncedBusqueda) {
                const s = debouncedBusqueda.toLowerCase();
                const mNombre = `${row.clienteNombre || ''} ${row.clienteApellido || ''}`.toLowerCase().includes(s);
                const mPoliza = String(row.nPoliza || '').includes(s);
                const mDoc = String(row.clienteDni || row.clienteCuit || '').includes(s);
                if (!mNombre && !mPoliza && !mDoc) return false;
            }

            // Comparador unificado de opciones seleccionadas
            const matchMulti = (campoKey, recordKey) => {
                const seleccionados = multiFiltros[campoKey] || [];
                if (seleccionados.length === 0) return true;
                
                let valorRecord = '';
                if (recordKey === 'canalNombreUnified') {
                    valorRecord = row.canalNombre || row.subcanal;
                } else if (recordKey === 'estadoGestionCobranzaUnified') {
                    valorRecord = row.estado || row.estadoGestion;
                } else if (recordKey === 'motivoUnified') {
                    valorRecord = row.motivoNuevo || row.motivo || row.tipoOperacion;
                } else if (recordKey === 'subOperacionUnified') {
                    valorRecord = row.subOperacionNueva || row.subOperacion;
                } else {
                    valorRecord = row[recordKey];
                }
                
                const normalizedRecordVal = String(valorRecord || '').toUpperCase().trim();
                return seleccionados.includes(normalizedRecordVal);
            };

            // Filtros estándar de Póliza / Generales
            if (!matchMulti('compania', 'compania') ||
                !matchMulti('riesgo', 'riesgo') ||
                !matchMulti('subriesgo', 'subriesgo') ||
                !matchMulti('canal', 'canal') ||
                !matchMulti('canalNombre', 'canalNombreUnified') ||
                !matchMulti('subcanalNombre', 'subcanalNombre') ||
                !matchMulti('estadoEmision', 'estadoEmision') ||
                !matchMulti('formaPago', 'formaPago') ||
                !matchMulti('motivo', 'motivoUnified') ||
                !matchMulti('subOperacion', 'subOperacionUnified') ||
                !matchMulti('tipoFacturacion', 'tipoFacturacion') ||
                !matchMulti('vendedor', 'vendedor') ||
                !matchMulti('suscriptor', 'suscriptor') ||
                !matchMulti('moneda', 'moneda')
            ) {
                return false;
            }

            // Filtros exclusivos de Cobranza
            if (moduloRaiz === 'COBRANZA') {
                if (!matchMulti('estadoGestionCobranza', 'estadoGestionCobranzaUnified') ||
                    !matchMulti('quienCobro', 'quienCobro') ||
                    !matchMulti('quienInformo', 'quienInformo') ||
                    !matchMulti('estadoDeuda', 'estadoDeuda')
                ) {
                    return false;
                }
            }

            // Filtros exclusivos de Renovaciones
            if (moduloRaiz === 'RENOVACIONES') {
                if (!matchMulti('estadoGestionRenovacion', 'estadoGestionRenovacion') ||
                    !matchMulti('quienRenovo', 'quienRenovo') ||
                    !matchMulti('tipoRenovacion', 'tipoRenovacion') ||
                    !matchMulti('formaRenovacion', 'formaRenovacion')
                ) {
                    return false;
                }
            }

            return true;
        });
    }, [moduloRaiz, dbState, multiFiltros, debouncedBusqueda, isDbLoaded]);

    return {
        multiFiltros,
        setMultiFiltros,
        resetFiltros,
        opcionesFiltrosUnicas,
        registrosProcesados
    };
};